// features/workstations/dashboard-sections/jarvis-briefing.js
// ═══════════════════════════════════════════════════════════
// JARVIS MORNING BRIEFING — composes hybrid primitives
// ═══════════════════════════════════════════════════════════
// Body content built from shared primitives in
// components/widgets/primitives. No widget-specific styling
// needed — the primitives ARE the design language.
//
// Future widgets (Macro Gate, Pipeline Intelligence, etc.) will
// import the same primitives so changes propagate everywhere.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { useWidgetFooterAction } from '../../../components/widgets/widget-card';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  ActionChip,
  MisdirRow,
  PossibRow,
  PillRow,
  PillTable,
  PractitionerLens,
  EvidenceList,
  Toolbar,
  ToolbarButton,
  ToolbarMeta,
} from '../../../components/widgets/primitives';
import EngineActionBar from './engine-action-bar';

const STATE_TONES = {
  strengthening: 'sage',
  holding:       'steel',
  weakening:     'amber',
  contradicted:  'terra',
  active:        'gold',
};

export default function JarvisBriefingSection({ workstation, userId }) {
  const [briefing, setBriefing] = useState(null);
  const [options, setOptions] = useState([]);
  const [tensions, setTensions] = useState([]);
  const [possibilities, setPossibilities] = useState([]);
  const [calibration, setCalibration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  // ─── Load today's briefing + engine outputs ───
  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return null;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        intent: 'morning_briefing',
      });
      const res = await fetch(`/api/jarvis/briefing?${qs}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : { briefing: null, options: [], tensions: [], possibilities: [] };
      setBriefing(data.briefing);
      setOptions(Array.isArray(data.options) ? data.options : []);
      setTensions(Array.isArray(data.tensions) ? data.tensions : []);
      setPossibilities(Array.isArray(data.possibilities) ? data.possibilities : []);

      // Calibration stats (Engine 5) — fire-and-forget alongside
      if (workstation?.domain_key) {
        fetchCalibration(userId, workstation.domain_key)
          .then(setCalibration)
          .catch(() => {});
      }
      return data.briefing;
    } catch (e) {
      console.warn('[jarvis-briefing] load failed:', e?.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, workstation?.domain_key, userId]);

  useEffect(() => { load(); }, [load]);

  // ─── Generate ───
  const handleGenerate = useCallback(async (forceRegen = false) => {
    if (!workstation?.id || !userId || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch('/api/jarvis/briefing?action=generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          workstation_id: workstation.id,
          intent: 'morning_briefing',
          force_regenerate: forceRegen,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        // Commit 69 — clear localStorage autogen flag on error
        // so the next visit retries instead of being blocked.
        try {
          if (typeof window !== 'undefined') {
            const todayKey = etDateKey();
            const lsKey = `ws:jarvis-briefing:autogen:${userId}:${workstation.id}:${todayKey}`;
            window.localStorage.removeItem(lsKey);
          }
        } catch { /* ignore */ }
        return;
      }
      setBriefing(data.briefing);
      // Fresh generation returns options directly; tensions come from
      // the tensionPass. Re-load the full view so we get canonical
      // server-side rows with ids for the action bars.
      load();
    } catch (e) {
      setError(e?.message || 'Generation failed');
      // Same retry-clear on network errors
      try {
        if (typeof window !== 'undefined') {
          const todayKey = etDateKey();
          const lsKey = `ws:jarvis-briefing:autogen:${userId}:${workstation.id}:${todayKey}`;
          window.localStorage.removeItem(lsKey);
        }
      } catch { /* ignore */ }
    } finally {
      setGenerating(false);
    }
  }, [workstation?.id, userId, generating, load]);

  // ─── Auto-generate once per day ───
  useEffect(() => {
    if (loading || generating || briefing) return;
    if (!workstation?.id || !userId) return;
    const todayKey = etDateKey();
    const lsKey = `ws:jarvis-briefing:autogen:${userId}:${workstation.id}:${todayKey}`;
    try {
      if (typeof window !== 'undefined' && window.localStorage.getItem(lsKey) === '1') return;
    } catch { /* SSR or storage blocked */ }
    const hourET = etHour();
    if (hourET == null || hourET < 6 || hourET > 20) return;
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem(lsKey, '1');
    } catch { /* best-effort */ }
    handleGenerate(false).catch(() => {});
  }, [loading, briefing, workstation?.id, userId, generating, handleGenerate]);

  // ─── Render ───
  return (
    <section className="ws-jbrief">
      {loading && <div className="ws-jbrief-loading">Loading…</div>}

      {!loading && !briefing && (
        <EmptyState onGenerate={() => handleGenerate(false)} generating={generating} error={error} />
      )}

      {!loading && briefing && (
        <BriefingBody
          briefing={briefing}
          options={options}
          tensions={tensions}
          possibilities={possibilities}
          calibration={calibration}
          userId={userId}
          onRegenerate={() => handleGenerate(true)}
          generating={generating}
          error={error}
        />
      )}
    </section>
  );
}

// Small helper so the widget doesn't need calibration state machinery
async function fetchCalibration(userId, domain) {
  const qs = new URLSearchParams({ user_id: userId, domain });
  const res = await fetch(`/api/engines/calibration?${qs}`, { cache: 'no-store' });
  if (!res.ok) return null;
  return res.json();
}

// ═══════════════════════════════════════════════════════════
// EMPTY STATE — widget-specific (not a primitive yet)
// ═══════════════════════════════════════════════════════════
function EmptyState({ onGenerate, generating, error }) {
  return (
    <div className="ws-jbrief-empty">
      <Eyebrow>no briefing yet</Eyebrow>
      <SerifHeadline>Ready when you are.</SerifHeadline>
      <SerifSummary>
        I&rsquo;ll read everything from the last 24 hours — your domain&rsquo;s
        news, your active theses, your recent activity — and produce: what&rsquo;s
        happening, options for action, options for planning, misdirections
        you&rsquo;re missing, and possibilities you haven&rsquo;t seen.
      </SerifSummary>
      <button
        type="button"
        className="ws-jbrief-empty-btn"
        onClick={onGenerate}
        disabled={generating}
      >
        {generating ? 'Thinking…' : '+ Generate today’s briefing'}
      </button>
      {error && <div className="ws-jbrief-error">{error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY — composed entirely from primitives
// ═══════════════════════════════════════════════════════════
function BriefingBody({
  briefing, options = [], tensions = [], possibilities = [], calibration = null,
  userId, onRegenerate, generating, error,
}) {
  const s1 = briefing.section_1_domain_briefing || {};
  const actions = arrayOf(briefing.section_2_action_options);
  const plans = arrayOf(briefing.section_3_planning_options);
  const misdirs = arrayOf(briefing.section_4_misdirections);
  const possibs = arrayOf(briefing.section_5_possibilities);
  const thesisStates = arrayOf(briefing.thesis_states);

  // Index engine rows by id for O(1) lookup in the section mappers
  const optionsById = {};
  for (const o of options) if (o?.id) optionsById[o.id] = o;
  const tensionsById = {};
  for (const t of tensions) if (t?.id) tensionsById[t.id] = t;
  const possibilitiesById = {};
  for (const p of possibilities) if (p?.id) possibilitiesById[p.id] = p;

  const conf = typeof briefing.confidence_overall === 'number'
    ? Math.round(briefing.confidence_overall * 100)
    : null;
  const dateStr = briefing.briefing_date || briefing.created_at?.slice(0, 10);
  const formattedDate = formatBriefingDate(dateStr);

  // Calibration badge — only renders if we have enough resolved samples
  const calibBadge = formatCalibrationBadge(calibration);

  return (
    <div className="ws-jbrief-body">
      <Eyebrow parts={[
        'current briefing',
        formattedDate,
        conf != null ? `${conf}% confident` : null,
        calibBadge,
      ]} />

      <SerifHeadline>{briefing.title}</SerifHeadline>
      <SerifSummary>{briefing.summary}</SerifSummary>

      {/* Commit 69 — Regenerate moved to footer (Tim's request).
          Toolbar here keeps just the delta-from-prior context. */}
      {briefing.delta_from_prior && (
        <Toolbar>
          <ToolbarMeta>{briefing.delta_from_prior}</ToolbarMeta>
        </Toolbar>
      )}

      {error && <div className="ws-jbrief-error">{error}</div>}

      {/* SECTION 1 — What's Happening */}
      {(s1.headline || s1.body) && (
        <SectionStrip label="what's happening">
          {s1.headline && <div className="ws-jbrief-s1-headline">{s1.headline}</div>}
          {s1.body && <div className="ws-jbrief-s1-body">{s1.body}</div>}
          <EvidenceList items={Array.isArray(s1.evidence) ? s1.evidence : []} />
          {s1.one_question && (
            <PractitionerLens label="one question">{s1.one_question}</PractitionerLens>
          )}
        </SectionStrip>
      )}

      {/* SECTION 2 — Action Options (hydrated from Engine 4 rows when linked) */}
      {actions.length > 0 && (
        <SectionStrip label="action options" count={actions.length}>
          <div className="ws-jbrief-chip-list">
            {actions.map((opt, i) => {
              const full = opt.option_id ? optionsById[opt.option_id] : null;
              const merged = full ? mergeOption(opt, full) : opt;
              return (
                <div key={i} className="ws-jbrief-chip-wrap">
                  <ActionChip option={merged} variant="action" />
                  {full?.id && (
                    <EngineActionBar kind="option" id={full.id} userId={userId} />
                  )}
                </div>
              );
            })}
          </div>
        </SectionStrip>
      )}

      {/* SECTION 3 — Planning Options */}
      {plans.length > 0 && (
        <SectionStrip label="planning options" count={plans.length}>
          <div className="ws-jbrief-chip-list">
            {plans.map((opt, i) => {
              const full = opt.option_id ? optionsById[opt.option_id] : null;
              const merged = full ? mergeOption(opt, full) : opt;
              return (
                <div key={i} className="ws-jbrief-chip-wrap">
                  <ActionChip option={merged} variant="planning" />
                  {full?.id && (
                    <EngineActionBar kind="option" id={full.id} userId={userId} />
                  )}
                </div>
              );
            })}
          </div>
        </SectionStrip>
      )}

      {/* SECTION 4 — Misdirections (hydrated from Engine 3 Tension rows when linked) */}
      {misdirs.length > 0 && (
        <SectionStrip label="misdirections + things you're missing" count={misdirs.length}>
          <div className="ws-jbrief-row-list">
            {misdirs.map((m, i) => {
              const tension = m.tension_id ? tensionsById[m.tension_id] : null;
              const merged = tension ? mergeMisdir(m, tension) : m;
              return (
                <div key={i} className="ws-jbrief-misdir-wrap">
                  <MisdirRow item={merged} />
                  {tension?.id && (
                    <EngineActionBar kind="tension" id={tension.id} userId={userId} />
                  )}
                </div>
              );
            })}
          </div>
        </SectionStrip>
      )}

      {/* SECTION 5 — Possibilities (hydrated from Engine 6 rows when linked) */}
      {possibs.length > 0 && (
        <SectionStrip label="possibilities" count={possibs.length}>
          <div className="ws-jbrief-row-list">
            {possibs.map((p, i) => {
              const full = p.possibility_id ? possibilitiesById[p.possibility_id] : null;
              const merged = full ? mergePossibility(p, full) : p;
              return (
                <div key={i} className="ws-jbrief-possib-wrap">
                  <PossibRow item={merged} />
                  {full?.id && (
                    <EngineActionBar kind="possibility" id={full.id} userId={userId} />
                  )}
                </div>
              );
            })}
          </div>
        </SectionStrip>
      )}

      {/* Thesis stress test — pill rows */}
      {thesisStates.length > 0 && (
        <SectionStrip label="active theses" count={thesisStates.length}>
          <PillTable>
            {thesisStates.map((t, i) => (
              <PillRow
                key={i}
                keyText={t.thesis_short || t.premise || '(unnamed thesis)'}
                valueText={t.state || 'active'}
                tone={STATE_TONES[t.state] || 'steel'}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Confidence drivers — inline lens at bottom */}
      {briefing.confidence_drivers && (
        <PractitionerLens label="confidence drivers">
          {typeof briefing.confidence_drivers === 'string'
            ? briefing.confidence_drivers
            : briefing.confidence_drivers.text || JSON.stringify(briefing.confidence_drivers)}
        </PractitionerLens>
      )}

      {/* Commit 72 — body-internal footer removed; freshness line
          stays in the body, but the Regenerate button is injected
          into the WidgetCard chrome footer-right via context (see
          BriefingFooterAction). */}
      {briefing.briefing_date && (
        <div className="ws-jbrief-freshness">
          briefing · <strong>{briefing.briefing_date}</strong>
          {briefing.briefing_at && (
            <> · generated {new Date(briefing.briefing_at).toLocaleString('en-US', {
              month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
              timeZone: 'America/New_York',
            })} ET</>
          )}
        </div>
      )}

      <BriefingFooterAction onRegenerate={onRegenerate} generating={generating} />
    </div>
  );
}

// ─── Commit 72 — injects Regenerate into WidgetCard footer-right
function BriefingFooterAction({ onRegenerate, generating }) {
  const setFooterAction = useWidgetFooterAction();
  useEffect(() => {
    setFooterAction(
      <button
        type="button"
        className="ws-card-footer-action-btn"
        onClick={onRegenerate}
        disabled={generating}
        title="Regenerate with latest context"
      >
        {generating ? 'Regenerating…' : '↻ Regenerate'}
      </button>
    );
    return () => setFooterAction(null);
  }, [setFooterAction, onRegenerate, generating]);
  return null;
}

// ─── Helpers ───
function arrayOf(v) { return Array.isArray(v) ? v : []; }

// Merge a briefing-section-2 item with its canonical Engine-4 option
// row. Prefer the full row's fields (they were generated at Engine-4
// time with strict schema) but keep the short inline fields as
// fallback.
function mergeOption(short, full) {
  return {
    what: full.what || short.what,
    why_now: full.why_now || short.why_now,
    assumptions: Array.isArray(full.assumptions) && full.assumptions.length
      ? full.assumptions
      : (Array.isArray(short.assumptions) ? short.assumptions : []),
    trade_off: full.trade_off || short.trade_off,
    confidence: typeof full.confidence === 'number' ? full.confidence : short.confidence,
    confidence_drivers: full.confidence_drivers || short.confidence_drivers,
    horizon: full.horizon || short.horizon,
    // Keep the linked ids visible in the chip context
    option_id: full.id,
  };
}

// Merge a briefing-section-4 misdirection with its Engine-3 Tension
// row. The tension row has canonical severity + suggested_resolution
// + body; the inline item was derived from it.
function mergeMisdir(short, tension) {
  return {
    what: tension.headline || short.what,
    severity: tension.severity || short.severity,
    source: tension.rule_id ? `rule ${tension.rule_id}` : short.source,
    suggested_resolution: tension.suggested_resolution || short.suggested_resolution,
    tension_id: tension.id,
  };
}

// Merge a briefing-section-5 possibility item with its Engine-6 row.
function mergePossibility(short, full) {
  return {
    observation: full.observation || short.observation,
    analog: full.analog || short.analog,
    evidence: Array.isArray(full.evidence) && full.evidence.length
      ? full.evidence
      : (Array.isArray(short.evidence) ? short.evidence : []),
    suggested_next_step: full.suggested_next_step || short.suggested_next_step,
    possibility_id: full.id,
    analog_type: full.analog_type,
    confidence: typeof full.confidence === 'number' ? full.confidence : short.confidence,
  };
}

// Return a short "calibrated" badge string for the eyebrow, or null
// if not enough resolved samples to be meaningful.
function formatCalibrationBadge(calibration) {
  if (!calibration || !calibration.aggregate) return null;
  const ag = calibration.aggregate;
  if (!ag.ai_resolved || ag.ai_resolved < 3) return null;
  // Prefer the high-confidence band if available
  const hi = (ag.ai_bucket || []).find(b => !b.too_few && b.band === '≥66%');
  if (hi && typeof hi.actual === 'number') {
    return `calibration: ${Math.round(hi.actual * 100)}% when engine said ≥66% (n=${hi.n})`;
  }
  // Fallback to first non-too-few bucket
  const any = (ag.ai_bucket || []).find(b => !b.too_few);
  if (any && typeof any.actual === 'number') {
    return `calibration: ${Math.round(any.actual * 100)}% in ${any.band} band (n=${any.n})`;
  }
  return null;
}

function formatBriefingDate(s) {
  if (!s) return '';
  try {
    const d = new Date(s + 'T00:00:00Z');
    if (Number.isNaN(d.getTime())) return s;
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString('en-US', opts).toLowerCase();
  } catch {
    return s;
  }
}

function etDateKey() {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });
    return fmt.format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}
function etHour() {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false,
    });
    const parts = fmt.formatToParts(new Date());
    const h = parts.find((p) => p.type === 'hour')?.value;
    return h != null ? parseInt(h, 10) : null;
  } catch {
    return null;
  }
}
